import styled from '@emotion/styled/macro';
import { useEffect, useState } from 'react';
import {
  getSplatUserType,
  isRetailUser,
  isSunrunManaged,
  UserProfile,
  FIELD_SALES,
  FUSION_CLOSER,
  INSIDE_SALES,
  DIRECT_TO_HOME,
} from '@sunrun/sales-experience-shared';
import {
  LoadingOverlay,
  SalesToolsWarning,
  useSingletonMobileMediaQuery,
} from '@sunrun/experience-ui-components';
import { GreetingHeader } from 'components/organisms/GreetingHeader';
import { MetricsGrid } from 'components/organisms/MetricsGrid';
import { QuickLinks } from 'components/organisms/QuickLinks';
// eslint-disable-next-line import/no-restricted-paths
import { DashboardOverview } from 'components/pages/DashboardOverview';
import { DashboardMetric, getMetrics } from 'providers/Metrics';
import { useHybridToken } from 'providers/Auth';
import { RetailAdvisorDashboard } from 'components/organisms/RetailAdvisorDashboard';

const Overview = () => {
  const [metrics, setMetrics] = useState(
    new Array(5).fill({
      icon: null,
      label: null,
      href: null,
      value: null,
    }) as DashboardMetric,
  );
  const isMobile = useSingletonMobileMediaQuery();
  const [userProfile, setUserProfile] = useState({} as UserProfile);
  const { hybridToken } = useHybridToken();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hybridToken) {
      setLoading(true);
      getMetrics(hybridToken)
        .then((res) => {
          setUserProfile(res.userProfile);
          setMetrics(res.metrics);
        })
        .catch(() => {
          // TODO: Fix under SPLAT1-7039
          // Do nothing
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [hybridToken]);

  const now = new window.Date();
  const splatUserType = getSplatUserType(userProfile);
  const isChannelPartner = splatUserType === 'Integrated Partner';
  const isCEA = isRetailUser(splatUserType);
  const isSRM = isSunrunManaged(userProfile);
  const isD2H = splatUserType === 'Direct to Home';
  const isFusion = userProfile.partnerName === 'Fusion';
  const isFusionAthlete = splatUserType === 'Fusion Setter';

  const isMetricsVisible =
    JSON.stringify(userProfile) !== '{}' &&
    !isChannelPartner &&
    !isCEA &&
    !isFusionAthlete;

  const shouldHideWarning =
    !userProfile?.title?.toLocaleLowerCase().includes('athlete') &&
    !isCEA &&
    userProfile?.sellsSunrun?.toLocaleLowerCase() === 'no'
      ? 'No'
      : 'Yes';

  if (loading) {
    return <LoadingOverlay message="Loading..." />;
  }

  const newDashboardDivisions = ['D2H', FIELD_SALES, INSIDE_SALES];

  const newDashboardUserTypes = [
    FUSION_CLOSER,
    DIRECT_TO_HOME,
    FIELD_SALES,
    INSIDE_SALES,
  ];

  const showNewDashboard =
    newDashboardDivisions.includes(userProfile?.division) ||
    newDashboardUserTypes.includes(splatUserType);

  if (showNewDashboard) {
    return (
      <OverviewContainer isMobile={isMobile}>
        <DashboardOverview
          isSRM={isSRM}
          userProfile={userProfile}
          warnings={<SalesToolsWarning sellsSunrun={shouldHideWarning} />}
        />
      </OverviewContainer>
    );
  }

  // Otherwise, show the original overview
  return (
    <OverviewContainer
      isMobile={isMobile}
      data-testid="old-dashboard-container">
      <Dashboard>
        <SalesToolsWarning sellsSunrun={shouldHideWarning} />
        <GreetingHeader userProfile={userProfile} date={now}></GreetingHeader>
        {isCEA && <RetailAdvisorDashboard />}
        <DashboardValues>
          {isMetricsVisible && <MetricsGrid metrics={metrics}></MetricsGrid>}
          <QuickLinks
            isChannelPartner={isChannelPartner}
            isCEA={isCEA}
            isD2H={isD2H}
            isFusion={isFusion}
            isSRM={isSRM}
            userProfile={userProfile as UserProfile}
          />
        </DashboardValues>
      </Dashboard>
    </OverviewContainer>
  );
};

const OverviewContainer = styled.div<{ isMobile: boolean }>`
  scrollbar-gutter: 16px;
  padding: ${({ isMobile }) => (isMobile ? '24px 16px' : '24px 40px 40px')};
  display: flex;
  justify-content: flex-start;
`;

const DashboardValues = styled.div`
  display: flex;
  gap: 32px;
  @media (max-width: 880px) {
    flex-direction: column;
  }
`;
const Dashboard = styled.div``;

export { Overview };
